import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Inventory,
  LocalShipping,
  AttachMoney,
  Assessment,
  Warning as WarningIcon,
  Nature as EcoIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { useGetAnalyticsOverviewQuery } from '../../store/api';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface RevenueTrendData {
  month: string;
  year: number;
  revenue: number;
  orders: number;
  avg_order_value: number;
}

interface TopProduct {
  name: string;
  revenue: number;
  units: number;
  growth: string;
}

const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const { data: overview, isLoading, error } = useGetAnalyticsOverviewQuery();
  
  const [revenueTrend, setRevenueTrend] = useState<RevenueTrendData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [chartsLoading, setChartsLoading] = useState(true);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const [trendRes, productsRes] = await Promise.all([
          fetch(`${API_BASE}/api/v1/analytics/revenue-trend`),
          fetch(`${API_BASE}/api/v1/analytics/top-products`),
        ]);
        
        if (trendRes.ok) {
          const trendData = await trendRes.json();
          setRevenueTrend(trendData.data || []);
        }
        
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setTopProducts(productsData.products || []);
        }
      } catch (err) {
        console.error('Failed to fetch chart data:', err);
      } finally {
        setChartsLoading(false);
      }
    };
    
    fetchChartData();
  }, []);

  const getTrendIcon = (trend: string) => {
    return trend === 'up' ? <TrendingUp /> : <TrendingUp sx={{ transform: 'rotate(180deg)' }} />;
  };

  const metrics = [
    {
      title: 'Total Revenue',
      value: overview?.revenue?.value || '£0',
      change: overview?.revenue?.change || '0%',
      trend: overview?.revenue?.trend || 'neutral',
      icon: <AttachMoney />,
      color: theme.palette.success.main,
    },
    {
      title: 'Inventory Value',
      value: overview?.inventory_value?.value || '£0',
      change: overview?.inventory_value?.change || '0%',
      trend: overview?.inventory_value?.trend || 'neutral',
      icon: <Inventory />,
      color: theme.palette.info.main,
    },
    {
      title: 'Active Suppliers',
      value: overview?.active_suppliers?.value || '0',
      change: overview?.active_suppliers?.change || '0',
      trend: overview?.active_suppliers?.trend || 'neutral',
      icon: <LocalShipping />,
      color: theme.palette.primary.main,
    },
    {
      title: 'Forecast Accuracy',
      value: overview?.forecast_accuracy?.value || '0%',
      change: overview?.forecast_accuracy?.change || '0%',
      trend: overview?.forecast_accuracy?.trend || 'neutral',
      icon: <Assessment />,
      color: theme.palette.secondary.main,
    },
  ];

  // Risk indicators for supply chain resilience
  const riskMetrics = [
    { label: 'Supplier Risk', score: 'Low', color: 'success' as const },
    { label: 'Inventory Risk', score: 'Medium', color: 'warning' as const },
    { label: 'Demand Volatility', score: 'Low', color: 'success' as const },
  ];

  // Sustainability metrics for environmental impact
  const sustainabilityMetrics = [
    { label: 'Carbon Footprint', value: '12.5 tons CO2', change: '-8%' },
    { label: 'Recycled Materials', value: '45%', change: '+5%' },
    { label: 'Energy Efficiency', value: '78%', change: '+3%' },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load analytics data</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Supply chain performance metrics and insights
        </Typography>
      </Box>

      {/* Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(metric.color, 0.1),
                      color: metric.color,
                    }}
                  >
                    {metric.icon}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: metric.change.startsWith('+') ? theme.palette.success.main : theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  >
                    {metric.change}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {metric.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {metric.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Risk and Sustainability Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Risk Indicators */}
        <Grid item xs={12} md={6}>
          <Card data-testid="risk" className="risk-indicator">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                <Typography variant="h6" fontWeight={600}>Risk Indicators</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {riskMetrics.map((risk, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{risk.label}</Typography>
                    <Chip 
                      label={risk.score} 
                      size="small" 
                      color={risk.color}
                      className="risk-score"
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sustainability Metrics */}
        <Grid item xs={12} md={6}>
          <Card data-testid="sustainability" className="sustainability">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EcoIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                <Typography variant="h6" fontWeight={600}>Sustainability Metrics</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {sustainabilityMetrics.map((metric, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{metric.label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{metric.value}</Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: metric.change.startsWith('+') || metric.change.startsWith('-') 
                            ? (metric.change.includes('-') ? theme.palette.success.main : theme.palette.success.main)
                            : theme.palette.text.secondary 
                        }}
                      >
                        {metric.change}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Revenue Trend
              </Typography>
              {chartsLoading ? (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : revenueTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis 
                      dataKey="month" 
                      stroke={theme.palette.text.secondary}
                      fontSize={12}
                    />
                    <YAxis 
                      stroke={theme.palette.text.secondary}
                      fontSize={12}
                      tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      formatter={(value) => [`£${Number(value).toLocaleString()}`, 'Revenue']}
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke={theme.palette.primary.main} 
                      strokeWidth={3}
                      dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: theme.palette.primary.dark }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No revenue data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top Products
              </Typography>
              {chartsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : topProducts.length > 0 ? (
                <List dense sx={{ p: 0 }}>
                  {topProducts.map((product, index) => (
                    <ListItem 
                      key={index}
                      sx={{ 
                        px: 0, 
                        borderBottom: index < topProducts.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        py: 1.5
                      }}
                    >
                      <Box sx={{ 
                        width: 28, 
                        height: 28, 
                        borderRadius: '50%', 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        mr: 2
                      }}>
                        {index + 1}
                      </Box>
                      <ListItemText 
                        primary={
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 150 }}>
                            {product.name}
                          </Typography>
                        }
                        secondary={`${product.units.toLocaleString()} units`}
                      />
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={700}>
                          £{(product.revenue / 1000).toFixed(0)}k
                        </Typography>
                        <Chip 
                          label={product.growth} 
                          size="small"
                          sx={{ 
                            height: 20,
                            fontSize: 10,
                            bgcolor: product.growth.startsWith('+') 
                              ? alpha(theme.palette.success.main, 0.1) 
                              : alpha(theme.palette.error.main, 0.1),
                            color: product.growth.startsWith('+') 
                              ? theme.palette.success.main 
                              : theme.palette.error.main,
                          }}
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Inventory sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1 }} />
                  <Typography color="text.secondary">No product data</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsPage;
